import { useEffect, useState } from "react";

type DnsRecord = { record?: string; name?: string; type?: string; value?: string; ttl?: string; priority?: number; status?: string };
type Identity = {
  id: string; os_id: string; from_email: string | null; normalized_domain: string | null;
  resend_domain_id: string | null;
  provider_status: "pending" | "verifying" | "verified" | "failed";
  dns_records: DnsRecord[]; verified_at: string | null; created_at: string; updated_at: string;
} | null;

const REFRESH_MS = 15_000; // 15s — within the 10–30s window; MUST stay >= 10_000

const STATUS_PILL: Record<string, { label: string; cls: string }> = {
  pending:   { label: "Pending DNS", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  verifying: { label: "Verifying…",  cls: "bg-blue-50 text-blue-700 border-blue-200" },
  verified:  { label: "Verified",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  failed:    { label: "Failed",      cls: "bg-red-50 text-red-700 border-red-200" },
};
const INPUT_CLS = "w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 disabled:opacity-60";

export default function EmailSendingSettings({ osId }: { osId: string }) {
  const [identity, setIdentity] = useState<Identity>(null);
  const [loading, setLoading] = useState(true);
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState<"" | "submit" | "verify" | "refresh">("");
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState("");

  // Initial load.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/email/identity?workspace_id=${encodeURIComponent(osId)}`);
        if (res.ok && !cancelled) { const j = await res.json(); setIdentity(j.identity || null); }
      } catch { /* leave empty; user can retry */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [osId]);

  // ── Background status refresh — STRICT polling discipline ──────────────
  // HARD RULES (do not relax — this is where loop bugs live):
  //  • REFRESH_MS is 15s (>= the 10s floor; never sub-10s, never recursive/hot)
  //  • runs ONLY while status is pending|verifying; STOPS on verified|failed
  //  • PAUSES while the tab is hidden (document.hidden)
  //  • ALWAYS cleared on unmount / status change (clearInterval in cleanup)
  // Passive GET only — it NEVER triggers verify. The ONLY immediate verify is
  // the user clicking the Verify button.
  useEffect(() => {
    const s = identity?.provider_status;
    if (s !== "pending" && s !== "verifying") return;
    let cancelled = false;
    const id = setInterval(async () => {
      if (document.hidden) return;
      try {
        const res = await fetch(`/api/email/identity?workspace_id=${encodeURIComponent(osId)}`);
        if (!res.ok || cancelled) return;
        const j = await res.json();
        if (!cancelled) setIdentity(j.identity || null);
      } catch { /* transient — next tick retries */ }
    }, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [identity?.provider_status, osId]);

  const submit = async () => {
    setErr(""); setBusy("submit");
    try {
      const res = await fetch("/api/email/identity", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workspace_id: osId, from_email: fromEmail.trim(), from_name: fromName.trim() || undefined }),
      });
      if (res.status === 409) { setErr("That domain is already registered to another workspace."); return; }
      if (res.status === 400) { setErr("Enter a valid address like you@yourdomain.com."); return; }
      if (res.status === 502) {
        let code = ""; try { code = (await res.json())?.error || ""; } catch { /* non-JSON */ }
        setErr(code === "resend_key_missing" ? "Email sending isn't configured on the server yet." : "Couldn't reach the email provider. Try again."); return;
      }
      if (!res.ok) { setErr("Couldn't add the domain. Try again."); return; }
      const j = await res.json(); setIdentity(j.identity || null); setEditing(false);
    } catch { setErr("Network error. Try again."); }
    finally { setBusy(""); }
  };

  const verify = async () => {
    setErr(""); setBusy("verify");
    try {
      const res = await fetch("/api/email/identity/verify", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workspace_id: osId }),
      });
      if (!res.ok) { setErr("Couldn't verify yet. Add the DNS records, then try again."); return; }
      const j = await res.json(); setIdentity(j.identity || null);
    } catch { setErr("Network error. Try again."); }
    finally { setBusy(""); }
  };

  const refresh = async () => {
    setBusy("refresh");
    try {
      const res = await fetch(`/api/email/identity?workspace_id=${encodeURIComponent(osId)}`);
      if (res.ok) { const j = await res.json(); setIdentity(j.identity || null); }
    } catch { /* ignore */ }
    finally { setBusy(""); }
  };

  const copy = (key: string, value: string) => {
    try { void navigator.clipboard?.writeText(value); setCopied(key); setTimeout(() => setCopied(""), 1500); } catch { /* clipboard blocked */ }
  };

  if (loading) return <div className="bg-white border border-zinc-200 rounded-xl p-5"><p className="text-sm text-zinc-500">Loading…</p></div>;

  const pill = identity ? (STATUS_PILL[identity.provider_status] || STATUS_PILL.pending) : null;

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4">
      {(!identity || editing) ? (
        <>
          <p className="text-sm text-zinc-600">
            Send email from your own domain (e.g. you@yourcompany.com). Add an address, then add the
            DNS records we generate at your domain registrar and verify it.
          </p>
          <div>
            <label className="block text-sm text-zinc-900 mb-1">From address</label>
            <input value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="you@yourcompany.com" disabled={busy === "submit"} className={INPUT_CLS} />
          </div>
          <div>
            <label className="block text-sm text-zinc-900 mb-1">Display name (optional)</label>
            <input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Your Company" disabled={busy === "submit"} className={INPUT_CLS} />
          </div>
          <div className="flex items-center gap-3">
            <button onClick={submit} disabled={busy === "submit"} className="px-4 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium transition-colors disabled:opacity-60">
              {busy === "submit" ? "Adding…" : "Add domain"}
            </button>
            {editing && <button onClick={() => { setEditing(false); setErr(""); }} className="text-sm text-zinc-600 underline">Cancel</button>}
            {err && <span className="text-xs text-red-600">{err}</span>}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div className="text-sm text-zinc-900">
              <span className="font-medium">{identity.from_email}</span>
              {identity.normalized_domain && <span className="text-zinc-500"> · {identity.normalized_domain}</span>}
            </div>
            {pill && <span className={`text-xs px-2 py-1 rounded-full border ${pill.cls}`}>{pill.label}</span>}
          </div>

          {identity.provider_status === "verified" ? (
            <p className="text-xs text-zinc-500">
              Verified{identity.verified_at ? ` on ${new Date(identity.verified_at).toLocaleString()}` : ""}. The Operator can send from this address — you confirm each send.
            </p>
          ) : (
            <>
              {identity.provider_status === "failed" && (
                <p className="text-sm text-red-600">Verification failed. Double-check the records below at your registrar, then verify again — DNS can take time to propagate.</p>
              )}
              <p className="text-sm text-zinc-600">Add these records at your DNS provider, then click Verify:</p>
              <div className="border border-zinc-200 rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50 text-zinc-500">
                    <tr><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Value</th><th className="px-3 py-2" /></tr>
                  </thead>
                  <tbody>
                    {(identity.dns_records || []).map((rec, i) => (
                      <tr key={i} className="border-t border-zinc-100 align-top">
                        <td className="px-3 py-2 font-mono">{rec.type}</td>
                        <td className="px-3 py-2 font-mono break-all">{rec.name}</td>
                        <td className="px-3 py-2 font-mono break-all">{rec.value}</td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => copy(`r${i}`, String(rec.value || ""))} className="text-zinc-500 hover:text-zinc-900 underline">{copied === `r${i}` ? "Copied" : "Copy"}</button>
                        </td>
                      </tr>
                    ))}
                    {(!identity.dns_records || identity.dns_records.length === 0) && (
                      <tr><td colSpan={4} className="px-3 py-3 text-zinc-500">No DNS records returned. Try Refresh.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div className="flex items-center gap-3">
            {identity.provider_status !== "verified" && (
              <button onClick={verify} disabled={busy === "verify"} className="px-4 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium transition-colors disabled:opacity-60">
                {busy === "verify" ? "Verifying…" : "Verify"}
              </button>
            )}
            <button onClick={refresh} disabled={busy === "refresh"} className="text-sm text-zinc-600 underline disabled:opacity-60">
              {busy === "refresh" ? "Refreshing…" : "Refresh"}
            </button>
            <button onClick={() => { setEditing(true); setFromEmail(identity.from_email || ""); setErr(""); }} className="text-sm text-zinc-600 underline">Change address</button>
            {err && <span className="text-xs text-red-600">{err}</span>}
          </div>
        </>
      )}
    </div>
  );
}
